import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Paper, Button, useTheme } from "@mui/material";
import Container from "../../../components/Container";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import PaymentComponent from "../../../components/vanliferComponents/PaymentInfo/PaymentComponent";
import SuggestedTours from "../../../components/vanliferComponents/SuggestedTours/SuggestedTours";
import ErrorHandling from "../../../components/presentationalComponents/ErrorHandling";
import { TourLoadingSpinner } from "./TourLoadingSpinner";
import { fetchAllRegions } from "../../../store/Vanlifer/regionSlice";
import Main from "../../../layouts/Main";
import SearchToursContainer from "../../../components/vanliferComponents/Search/SearchToursContainer";
import { SearchFilterProvider } from "../../../components/vanliferComponents/Search/SearchFilterProvider";
import SearchToursCardGrid from "../../../components/vanliferComponents/Search/SearchToursCardGrid";
import DiscoverComponent from "../../../components/vanliferComponents/Community/DiscoverComponent";
import { chargebeePromise } from "../../../utils/chargebee";
import { CancelablePromise, SubscriptionService } from "../../../services/restserver-openapi";
import {
    getUserAsync,
    getUserEntitlementsAsync,
    getUserSubscriptionsAsync,
} from "../../../store/navBar/userSlice";
import BaseDialog from "../../../components/BaseDialog";
import { getAgentProducts, toSearchCardProducts } from '../../../utils/Agent/products';
import { getFuturePlannedToursCount } from '../../../utils/Vanlifer/tour';

import EarlyAccessModal from "../../../components/vanliferComponents/EarlyAccess/EarlyAccessModal";

function TravelPlanningPage() {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const showPaymentComponent = useAppSelector((state) => state.vanliferComponents.showPaymentComponent);

    const generatedTours = useAppSelector((state) => state.tour.generatedTours);
    const previousGeneratedTours = useAppSelector((state) => state.tour.previousGeneratedTours);
    const isLoading = useAppSelector((state) => state.tour.loading);
    const tourErrorState = useAppSelector((state) => state.tour.error);

    const userEntitlements = useAppSelector((state) => state.user.entitlements);
    const userRole = useAppSelector((state) => state.user.userRole);
    const userSpecificationId = useAppSelector((state) => state.user.userSpecificationId);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [curatedCards, setCuratedCards] = useState<ReturnType<typeof toSearchCardProducts>>([]);
    const [plannedToursCount, setPlannedToursCount] = useState(0);

    const lastSixTours = useMemo(() => previousGeneratedTours?.slice(-6), [previousGeneratedTours]);

    const maxToursPerDay = useMemo(() => {
        return userEntitlements.find((ent) => ent.featureId === "vanlifermaxdailygeneratedtours")?.value;
    }, [userEntitlements]);

    const maxPlannedTours = useMemo(() => {
        const rawValue = userEntitlements.find((ent) => ent.featureId === "vanlifermaxnrofplannedtours")?.value;
        if (!rawValue || rawValue.toLowerCase() === "unlimited") {
            return null;
        }

        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : null;
    }, [userEntitlements]);

    const plannedToursLimitReached = maxPlannedTours !== null && plannedToursCount >= maxPlannedTours;

    useEffect(() => {
        dispatch(fetchAllRegions());
    }, [dispatch]);

    useEffect(() => {
        const loadPlannedToursCount = async () => {
            try {
                const count = await getFuturePlannedToursCount();
                setPlannedToursCount(count);
            } catch {
                setPlannedToursCount(0);
            }
        };

        loadPlannedToursCount();
    }, []);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                const products = await getAgentProducts();
                setCuratedCards(toSearchCardProducts(products));
            } catch {
                setCuratedCards([]);
            }
        };

        loadProducts();
    }, []);

    // Öffne Modal sobald gültige Touren da sind (robuster Guard)
    useEffect(() => {
        const hasValidTours =
            Array.isArray(generatedTours) &&
            generatedTours.length > 0 &&
            generatedTours[0]?.stays?.length != null &&
            generatedTours[0].stays.length > 0;

        if (hasValidTours) {
            setDialogOpen(true);
        }
    }, [generatedTours]);

    const openSelfServicePortal = async () => {
        const cbInstance = await chargebeePromise;
        if (!cbInstance) return;

        const SelfServicePromise: CancelablePromise<any> = SubscriptionService.getPortal();
        cbInstance.setPortalSession(() => SelfServicePromise);

        const cbPortal = cbInstance.createChargebeePortal();
        cbPortal.open({
            close() {
                dispatch(getUserSubscriptionsAsync());
                dispatch(getUserAsync({ userSpecificationId, userRoleInput: userRole! }));
                dispatch(getUserEntitlementsAsync());
            },
        });
    };

    const NoToursFound = () => (
        <Grid item xs={12} container justifyContent="center" alignItems="flex-start" my={5}>
            <Grid item xs={12}>
                <Box>
                    {maxToursPerDay === "Unlimited" ? (
                        <Typography variant="h5" align="center">
                            Leider haben wir keine Reisevorschläge für Dich gefunden.
                        </Typography>
                    ) : (
                        <Paper sx={{ borderRadius: 5, backgroundColor: "secondary.light", p: 4 }}>
                            <Typography variant="h5" align="center">
                                Lust auf mehr?
                            </Typography>
                            <Typography variant="body1" align="center">
                                Du hast bereits {maxToursPerDay} Reisevorschläge generiert und damit das tägliche Limit erreicht.
                            </Typography>
                            <Typography variant="body1" align="center">
                                Mit unserer Early Access Mitgliedschaft genießt du unbegrenzten Zugang zum Reiseplaner und mehr.
                            </Typography>
                            <Box mt={3} display="flex" justifyContent="center">
                                <Button variant="outlined" color="primary" onClick={openSelfServicePortal}>
                                    Hol Dir Early Access
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Grid>

            {lastSixTours && lastSixTours.length > 0 && (
                <>
                    <Grid item xs={12} container justifyContent="center" mt={4}>
                        <Typography variant="h6" align="center">
                            Hier sind deine letzten 6 generierten Touren:
                        </Typography>
                    </Grid>
                    <SuggestedTours generatedTours={lastSixTours} onTourSelected={() => setDialogOpen(false)} />
                </>
            )}
        </Grid>
    );

    if (tourErrorState) return <ErrorHandling errorMessage={tourErrorState} />;
    if (showPaymentComponent) return <PaymentComponent />;

    return (
        <Main>
            <SearchFilterProvider>
                <Box bgcolor={theme.palette.alternate}>
                    <Container>
                        <SearchToursContainer
                            plannedToursLimitReached={plannedToursLimitReached}
                            plannedToursCount={plannedToursCount}
                            maxPlannedTours={maxPlannedTours}
                        />
                    </Container>
                </Box>

                <Box bgcolor={theme.palette.alternate}>
                    <Container>
                        <DiscoverComponent />
                    </Container>
                </Box>

                <Box bgcolor={theme.palette.alternate}>
                    <Container>
                        <SearchToursCardGrid
                            keyword={"Kuratierte Angebote"}
                            title={"Das Beste: Unsere "}
                            title_emph={"Empfehlungen"}
                            description={
                                "Jede Karte steht für eine Auswahl einzigartiger Erlebnisse – klick drauf und erhalte passende Reisen."
                            }
                            cards={curatedCards}
                        />
                    </Container>
                </Box>

                {/* Modal für Tour-Vorschläge */}
                <BaseDialog open={dialogOpen} onClose={() => setDialogOpen(false)} closeOnly maxWidth="lg">
                    {isLoading ? (
                        <Grid container justifyContent="center" alignItems="center" p={7}>
                            <Typography variant="h5">Deine Touren sind bald da...</Typography>
                            <TourLoadingSpinner />
                        </Grid>
                    ) : !generatedTours ? (
                        <></>
                    ) : generatedTours.length === 0 || generatedTours?.[0]?.stays?.length === 0 ? (
                        <NoToursFound />
                    ) : (
                        <SuggestedTours generatedTours={generatedTours} onTourSelected={() => setDialogOpen(false)} />
                    )}
                </BaseDialog>
            </SearchFilterProvider>
            <EarlyAccessModal />
        </Main>
    );
}

export default TravelPlanningPage;
